import type { LevelConfig, LevelPiece } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as the
// other showcases' local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

/**
 * Static test level for the sliding table (see table_slider.ts and
 * physics_table.ts #setSlide): a three-layer table at the BACK riding its stack
 * left and right, with an ordinary one-layer table parked in front of it, so
 * the moving deck can be read against something that stands still.
 *
 *   - only the deck travels. The leg keeps its spot on the floor, which is what
 *     makes the stroke read as a moving platform rather than the whole table
 *     walking off;
 *   - the blocks are carried through the stroke explicitly every physics step
 *     (TableSlider), the way TableElevator carries them through a lift;
 *   - the layout reserves the whole corridor the deck sweeps (tableClaim's
 *     slide_distance), so the travelling table never reaches its neighbour;
 *   - a block that ends up on the OTHER table changes hands: GameScreen's
 *     reassignBlockTables() re-reads which deck every block is standing over
 *     each physics step, so one knocked onto the moving deck starts travelling
 *     with it and one knocked off it stops dead.
 */
const COLS = 4;
const ROWS = 3;

function makeDeck( cols = COLS, rows = ROWS ): { grid: number[][]; pieces: LevelPiece[] }
{
  const grid: number[][] = [];
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let row = 0; row < rows; row++ )
  {
    const grid_row: number[] = [];

    for ( let col = 0; col < cols; col++ )
    {
      grid_row.push( id );
      pieces.push( { id, type: 1, x: col, y: row, width: 1, height: 1 } );
      id++;
    }

    grid.push( grid_row );
  }

  return { grid, pieces };
}

const SLIDE_TABLE_LEVEL: LevelConfig = {
  level: 90308,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  tables: [
    { layers: [ makeDeck(), makeDeck(), makeDeck() ], slot: { u: -1, v: 0 }, slide: true },
    // Deliberately two rows short of the stack behind it: both tables sit on the
    // same centre line, so a full-height one in front would simply hide the
    // travelling deck this level exists to show.
    { layers: [ makeDeck( COLS, 2 ) ], slot: { u: 1, v: 0 }, elevator: true },
  ],
};

export class SlideTableShowcase
{
  public static readonly LEVEL = SLIDE_TABLE_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? SLIDE_TABLE_LEVEL : null;
  }
}
